import {Helmet} from '@dr.pogodin/react-helmet';
import {openDeleteDialog} from 'platform/components';
import {match} from 'ts-pattern';

import {head, isNil} from 'ramda';
import {isArray} from 'ramda-adjunct';

import {useDeleteRoundingDefinitionMutation} from '@dms/api/finance';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsTemplate, SettingsTemplateHeader} from '@dms/settings/shared';
import {DmsDataGrid, catchUnhandledDataGridActions, handleApiError} from '@dms/shared';

import {composePath, useNavigate} from 'shared/web';

import type {ActionCallback} from 'features/eag-grid';

const PREDEFINED_COLUMNS = ['branch'] as const;

type BranchType = {
  id: string;
  label: string;
};

export function DocumentRoundingDefinitions() {
  const navigate = useNavigate();
  const [deleteRoundingDefinition] = useDeleteRoundingDefinitionMutation();

  const actionCallback: ActionCallback<typeof PREDEFINED_COLUMNS> = ({
    actionKey,
    rowId,
    refreshData,
    getColumnData,
  }) => {
    const id = isArray(rowId) ? head(rowId) : rowId;

    if (isNil(id)) {
      return;
    }

    const branch = getColumnData<BranchType>('branch')?.value;

    match(actionKey)
      .with('DETAIL', 'EDIT', () =>
        navigate(composePath(settingsRoutes.documentRoundingDefinitionsEdit, {params: {id}}))
      )
      .with('DELETE', () =>
        openDeleteDialog({
          text: i18n.t('entity.accounting.labels.deleteRoundingDefinitionConfirmationText', {
            branch: branch?.label,
          }),
          onConfirm: () =>
            deleteRoundingDefinition({id})
              .unwrap()
              .then(() => refreshData())
              .catch(handleApiError),
        })
      )
      .otherwise(() => catchUnhandledDataGridActions(actionKey));
  };

  const title = i18n.t('page.accounting.labels.documentRoundingDefinitions');

  const header: SettingsTemplateHeader = {
    title,
    actions: [
      {
        type: 'button',
        title: i18n.t('page.accounting.actions.addDefinition'),
        onClick: () => {
          navigate(settingsRoutes.documentRoundingDefinitionsAdd);
        },
        'data-testid': testIds.settings.documentRoundingDefinitionsEdit('createNew'),
      },
    ],
  };

  return (
    <>
      <Helmet title={title} />
      <SettingsTemplate header={header} data-testid="settings-document-rounding-definition-page">
        <DmsDataGrid
          gridCode="accounting-rounding-definition"
          actionCallback={actionCallback}
          predefinedColumns={PREDEFINED_COLUMNS}
          data-testid={testIds.settings.documentRoundingDefinitions('datagrid')}
        />
      </SettingsTemplate>
    </>
  );
}
